import { countBy, uniq, sortBy } from 'lodash/fp';
import { useQuery } from '@apollo/client';
import { graphql } from '@gql/gql';
import type {
  AccountGetAnimalSensitivitiesQuery,
  AccountGetAnimalSensitivitiesQueryVariables,
} from '@gql/graphql';
import { AnimalSensitivityEnum, AnimalTypeEnum } from '@gql/graphql';

import { useMemo } from 'react';

export const ACCOUNT_GET_ANIMAL_SENSITIVITIES = graphql(`
  query AccountGetAnimalSensitivities($where: AnimalWhereInput) {
    accountMe {
      id
      animals(where: $where) {
        sensitivities
      }
    }
  }
`);

export const useGetAccountAnimalSensitivities = () => {
  const { data, loading, refetch } = useQuery<
    AccountGetAnimalSensitivitiesQuery,
    AccountGetAnimalSensitivitiesQueryVariables
  >(ACCOUNT_GET_ANIMAL_SENSITIVITIES, {
    variables: {
      where: { type: { equals: AnimalTypeEnum.DOG } },
    },
  });

  const sensitivities = useMemo(() => {
    const allSensitivities = data?.accountMe?.animals
      ?.map((item) => item.sensitivities as AnimalSensitivityEnum[] | undefined)
      ?.flat();
    if (!allSensitivities?.length) return [];

    const filteredSensitivities = allSensitivities.filter(
      (x) => !!x && x !== AnimalSensitivityEnum.NONE,
    );
    if (!filteredSensitivities?.length) return [];

    const occurrences = countBy(filteredSensitivities);
    const uniqueSensitivities = uniq(filteredSensitivities);
    const sortedByOccurrence = sortBy(
      (sensiv) => occurrences[sensiv],
      uniqueSensitivities,
    ).reverse();

    return sortedByOccurrence;
  }, [data?.accountMe?.animals]);

  return {
    sensitivities,
    sensitivitiesLoading: loading,
    sensitivitiesRefetch: refetch,
  };
};
